import React, { useMemo, useState } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { Button, Chip } from '@mui/material';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import { RefreshCw } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck,
  faLayerGroup,
  faStar,
  faCircleXmark,
  faDiagramProject,
} from '@fortawesome/free-solid-svg-icons';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import DriveFileMoveRoundedIcon from '@mui/icons-material/DriveFileMoveRounded';
import PodcastsRoundedIcon from '@mui/icons-material/PodcastsRounded';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import CategoryFormDialogs from '../components/categories/CategoryFormDialogs';
import { CategoriesManager } from '../hooks/useCategoriesManager';
import { CategoryType, ContentCategory } from '../types';
import { getImageUrl } from '../utils/imageUtils';
import { getPublicCategoryBrowseUrl } from '../utils/publicSiteUrl';
import { BrandSpinnerRing } from '../components/SiteBrandedLoader';

const studioTheme = createTheme({
  direction: 'rtl',
  typography: {
    fontFamily: '"Vazirmatn", "Segoe UI", Tahoma, sans-serif',
  },
  palette: {
    mode: 'light',
    primary: { main: '#1C3B9B' },
    secondary: { main: '#C11277' },
  },
  shape: { borderRadius: 12 },
});

type Props = {
  manager: CategoriesManager;
};

type CatTree = ContentCategory & { children?: CatTree[] };

const tabConfig: { id: CategoryType; label: string; hint: string; Icon: typeof SchoolRoundedIcon }[] =
  [
    { id: 'COURSE', label: 'دوره‌ها', hint: 'ساختار نمایش دوره‌ها در سایت', Icon: SchoolRoundedIcon },
    {
      id: 'PODCAST',
      label: 'پادکست‌ها',
      hint: 'دسته‌های صوتی و محتوای شنیداری',
      Icon: PodcastsRoundedIcon,
    },
  ];

function filterCategoryTree(
  nodes: CatTree[],
  q: string,
  status: '' | 'active' | 'inactive',
  featuredOnly: boolean,
): CatTree[] {
  const term = q.trim().toLowerCase();
  const matchesStrict = (n: ContentCategory) => {
    if (status === 'active' && !n.isActive) return false;
    if (status === 'inactive' && n.isActive) return false;
    if (featuredOnly && !n.isFeatured) return false;
    if (!term) return true;
    const hay = `${(n.name || '').toLowerCase()} ${(n.slug || '').toLowerCase()} ${(n.path || '').toLowerCase()}`;
    return hay.includes(term);
  };

  const walk = (items: CatTree[]): CatTree[] => {
    const result: CatTree[] = [];
    for (const n of items) {
      const subs = walk((n.children as CatTree[]) || []);
      const selfOk = matchesStrict(n);
      if (selfOk || subs.length > 0) {
        result.push({ ...(n as ContentCategory), children: subs });
      }
    }
    return result;
  };

  return walk(nodes);
}

function StudioTreeNode({
  node,
  depth,
  contentType,
  onEdit,
  onMove,
  onDelete,
}: {
  node: CatTree;
  depth: number;
  contentType: CategoryType;
  onEdit: (c: ContentCategory) => void;
  onMove: (c: ContentCategory) => void;
  onDelete: (id: string) => void;
}) {
  const children = (node.children || []).slice().sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  const thumbUrl = getImageUrl(node.image || undefined);
  const slugOk = !!(node.slug && String(node.slug).trim());
  const publicBrowseHref = slugOk ? getPublicCategoryBrowseUrl(contentType, String(node.slug).trim()) : '';

  return (
    <li className="list-none">
      <div
        className="group relative rounded-2xl border border-slate-200/90 bg-white/90 p-4 shadow-sm shadow-slate-900/[0.04] backdrop-blur-sm transition-all duration-200 hover:border-slate-300 hover:shadow-md hover:shadow-slate-900/[0.07]"
        style={{ marginRight: depth === 0 ? 0 : Math.min(depth * 14, 56) }}
      >
        {depth > 0 && (
          <span
            className="pointer-events-none absolute -right-3 top-1/2 h-[calc(100%+8px)] w-px -translate-y-1/2 bg-gradient-to-b from-[#1C3B9B]/35 via-[#C11277]/25 to-transparent"
            aria-hidden
          />
        )}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0 flex-1 space-y-1.5">
            <div className="flex flex-wrap items-center gap-2">
              {thumbUrl ? (
                <img
                  src={thumbUrl}
                  alt=""
                  className="h-10 w-10 shrink-0 rounded-xl object-cover shadow-sm ring-1 ring-slate-200/80"
                />
              ) : (
                <span
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#1C3B9B]/12 to-[#C11277]/10 text-xs font-bold text-[#1C3B9B]"
                  aria-hidden
                >
                  {(node.name || '?').slice(0, 1)}
                </span>
              )}
              <span className="truncate text-base font-semibold text-[#0F172A]">{node.name}</span>
              <Chip
                size="small"
                label={node.isActive ? 'فعال' : 'غیرفعال'}
                sx={{
                  height: 24,
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  bgcolor: node.isActive ? 'rgba(28, 59, 155, 0.1)' : 'rgba(15, 23, 42, 0.06)',
                  color: node.isActive ? '#1C3B9B' : '#64748B',
                  border: 'none',
                }}
              />
              {node.isFeatured ? (
                <Chip
                  size="small"
                  label="ویژه"
                  sx={{
                    height: 24,
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    bgcolor: 'rgba(193, 18, 119, 0.12)',
                    color: '#C11277',
                    border: 'none',
                  }}
                />
              ) : null}
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[#475569]">
              <code className="rounded-md bg-slate-100 px-2 py-0.5 font-mono text-[11px] text-[#1C3B9B]">
                /{node.slug}
              </code>
              <span className="text-slate-400">|</span>
              <span className="truncate" title={node.path || ''}>
                مسیر: {node.path || 'ریشه'}
              </span>
            </div>
          </div>
          <div className="flex flex-shrink-0 flex-wrap gap-2">
            <Button
              size="small"
              variant="contained"
              color="success"
              disabled={!slugOk}
              startIcon={<OpenInNewRoundedIcon sx={{ fontSize: 18 }} />}
              onClick={() => {
                if (!publicBrowseHref) return;
                window.open(publicBrowseHref, '_blank', 'noopener,noreferrer');
              }}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
              title={
                slugOk
                  ? 'بازکردن پیش‌نمایش این دسته در سایت عمومی (تب جدید)'
                  : 'ابتدا برای این دسته اسلاگ تعریف کنید'
              }
            >
              مشاهده در سایت
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<EditRoundedIcon sx={{ fontSize: 18 }} />}
              onClick={() => onEdit(node)}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
            >
              ویرایش
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="warning"
              startIcon={<DriveFileMoveRoundedIcon sx={{ fontSize: 18 }} />}
              onClick={() => onMove(node)}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
            >
              جابه‌جایی
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} />}
              onClick={() => onDelete(node.id)}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
            >
              حذف
            </Button>
          </div>
        </div>
      </div>
      {children.length > 0 && (
        <ul className="mt-3 space-y-3 border-r border-dashed border-slate-200/80 pr-4">
          {children.map((child) => (
            <StudioTreeNode
              key={child.id}
              node={child as CatTree}
              depth={depth + 1}
              contentType={contentType}
              onEdit={onEdit}
              onMove={onMove}
              onDelete={onDelete}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

const categoryOverviewCards = [
  {
    key: 'total',
    label: 'کل در این بخش',
    cardClassName: 'border-blue-100 from-blue-50 to-white',
    labelClassName: 'text-blue-700',
    valueClassName: 'text-blue-900',
    iconWrapperClassName: 'bg-blue-100 text-blue-700',
    icon: faLayerGroup,
  },
  {
    key: 'active',
    label: 'فعال',
    cardClassName: 'border-emerald-100 from-emerald-50 to-white',
    labelClassName: 'text-emerald-700',
    valueClassName: 'text-emerald-900',
    iconWrapperClassName: 'bg-emerald-100 text-emerald-700',
    icon: faCircleCheck,
  },
  {
    key: 'inactive',
    label: 'غیرفعال',
    cardClassName: 'border-amber-100 from-amber-50 to-white',
    labelClassName: 'text-amber-700',
    valueClassName: 'text-amber-900',
    iconWrapperClassName: 'bg-amber-100 text-amber-700',
    icon: faCircleXmark,
  },
  {
    key: 'featured',
    label: 'ویژه',
    cardClassName: 'border-fuchsia-100 from-fuchsia-50 to-white',
    labelClassName: 'text-fuchsia-700',
    valueClassName: 'text-fuchsia-900',
    iconWrapperClassName: 'bg-fuchsia-100 text-fuchsia-700',
    icon: faStar,
  },
  {
    key: 'roots',
    label: 'دستهٔ ریشه',
    cardClassName: 'border-indigo-100 from-indigo-50 to-white',
    labelClassName: 'text-indigo-700',
    valueClassName: 'text-indigo-900',
    iconWrapperClassName: 'bg-indigo-100 text-indigo-700',
    icon: faDiagramProject,
  },
] as const;

const CategoryStudioView: React.FC<Props> = ({ manager }) => {
  const {
    error,
    setError,
    loading,
    categoryTree,
    activeTab,
    setActiveTab,
    categories,
    openCreate,
    openEdit,
    handleDelete,
    setMoveTarget,
    setMoveParentId,
    fetchCategories,
  } = manager;

  const [listSearch, setListSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'' | 'active' | 'inactive'>('');
  const [featuredOnly, setFeaturedOnly] = useState(false);

  const counts = tabConfig.map((t) => ({
    ...t,
    count: categories.filter((c) => c.type === t.id).length,
  }));

  const tabCats = useMemo(() => categories.filter((c) => c.type === activeTab), [categories, activeTab]);

  const overviewStats = useMemo(
    () => ({
      total: tabCats.length,
      active: tabCats.filter((c) => c.isActive).length,
      inactive: tabCats.filter((c) => !c.isActive).length,
      featured: tabCats.filter((c) => c.isFeatured).length,
      roots: categoryTree.length,
    }),
    [tabCats, categoryTree.length],
  );

  const filteredTree = useMemo(
    () =>
      filterCategoryTree(
        categoryTree as CatTree[],
        listSearch,
        statusFilter,
        featuredOnly,
      ),
    [categoryTree, listSearch, statusFilter, featuredOnly],
  );

  const activeListFilters =
    (listSearch.trim() ? 1 : 0) + (statusFilter ? 1 : 0) + (featuredOnly ? 1 : 0);

  const clearListFilters = () => {
    setListSearch('');
    setStatusFilter('');
    setFeaturedOnly(false);
  };

  return (
    <ThemeProvider theme={studioTheme}>
      <div className="category-studio-root space-y-5" style={{ fontFamily: '"Vazirmatn", "Segoe UI", Tahoma, sans-serif' }}>
        <div className="rounded-2xl bg-white p-6 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">مدیریت دسته‌بندی‌ها</h1>
              <p className="mt-1 text-sm text-gray-600">
                سلسلهٔ دوره‌ها و پادکست‌ها، مسیر صفحات و وضعیت نمایش را از همین صفحه کنترل کنید.
              </p>
            </div>
            <button
              type="button"
              onClick={() => openCreate()}
              className="inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-bold text-white shadow-sm transition-colors hover:bg-indigo-700"
            >
              <svg className="h-5 w-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              دستهٔ جدید
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-5">
          {categoryOverviewCards.map((item) => {
            const value = overviewStats[item.key as keyof typeof overviewStats];
            return (
              <div key={item.key} className={`rounded-2xl border bg-gradient-to-b p-4 ${item.cardClassName}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className={`text-xs font-semibold ${item.labelClassName}`}>{item.label}</p>
                    <p className={`mt-2 text-2xl font-black ${item.valueClassName}`}>
                      {value.toLocaleString('fa-IR')}
                    </p>
                  </div>
                  <span
                    className={`inline-flex h-9 w-9 items-center justify-center rounded-xl ${item.iconWrapperClassName}`}
                  >
                    <FontAwesomeIcon icon={item.icon} className="h-4 w-4" />
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        <div className="grid grid-cols-1 gap-3 rounded-2xl bg-white p-4 shadow-sm">
          <div className="flex flex-wrap gap-2 border-b border-slate-100 pb-4">
            {counts.map(({ id, label, hint, Icon, count }) => {
              const active = activeTab === id;
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => setActiveTab(id)}
                  className={`flex min-w-[160px] flex-1 items-center gap-3 rounded-xl border px-3 py-2 text-right transition-all ${
                    active
                      ? 'border-indigo-300 bg-indigo-50 shadow-sm'
                      : 'border-slate-200 bg-slate-50/80 hover:bg-white hover:border-slate-300'
                  }`}
                >
                  <span
                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                      active ? 'bg-indigo-600 text-white' : 'bg-white text-slate-500 shadow-sm'
                    }`}
                  >
                    <Icon sx={{ fontSize: 20 }} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center justify-between gap-2">
                      <span className={`text-sm font-bold ${active ? 'text-gray-900' : 'text-gray-700'}`}>
                        {label}
                      </span>
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-600">
                        {count.toLocaleString('fa-IR')}
                      </span>
                    </span>
                    <span className="mt-0.5 block truncate text-[11px] text-slate-500">{hint}</span>
                  </span>
                </button>
              );
            })}
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-6 pt-3">
            <div className="sm:col-span-2 xl:col-span-6 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  clearListFilters();
                  setStatusFilter('');
                }}
                className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100"
              >
                همهٔ وضعیت‌ها
              </button>
              <button
                type="button"
                onClick={() => {
                  clearListFilters();
                  setStatusFilter('active');
                }}
                className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 hover:bg-emerald-100"
              >
                فقط فعال
              </button>
              <button
                type="button"
                onClick={() => {
                  clearListFilters();
                  setStatusFilter('inactive');
                }}
                className="rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700 hover:bg-amber-100"
              >
                فقط غیرفعال
              </button>
              <button
                type="button"
                onClick={() => {
                  clearListFilters();
                  setFeaturedOnly(true);
                }}
                className="rounded-full border border-fuchsia-200 bg-fuchsia-50 px-3 py-1 text-xs font-semibold text-fuchsia-700 hover:bg-fuchsia-100"
              >
                فقط ویژه
              </button>
              {activeListFilters > 0 ? (
                <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-600">
                  {activeListFilters.toLocaleString('fa-IR')} فیلتر فعال
                </span>
              ) : null}
            </div>
            <input
              className="sm:col-span-2 rounded-xl border border-gray-200 px-3 py-2 text-sm"
              placeholder="جستجو: نام، اسلاگ یا مسیر"
              value={listSearch}
              onChange={(e) => setListSearch(e.target.value)}
            />
            <select
              className="rounded-xl border border-gray-200 px-3 py-2 text-sm"
              value={statusFilter}
              onChange={(e) => setStatusFilter((e.target.value || '') as '' | 'active' | 'inactive')}
            >
              <option value="">وضعیت: همه</option>
              <option value="active">فعال</option>
              <option value="inactive">غیرفعال</option>
            </select>
            <select
              className="rounded-xl border border-gray-200 px-3 py-2 text-sm"
              value={featuredOnly ? 'yes' : ''}
              onChange={(e) => setFeaturedOnly(e.target.value === 'yes')}
            >
              <option value="">ویژه: همه</option>
              <option value="yes">فقط ویژه</option>
            </select>
            <button
              type="button"
              onClick={() => void fetchCategories()}
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60"
            >
              {loading ? <BrandSpinnerRing size="sm" inverse /> : <RefreshCw className="h-4 w-4" aria-hidden />}
              بروزرسانی
            </button>
            <button
              type="button"
              onClick={clearListFilters}
              className="inline-flex items-center justify-center rounded-xl border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
            >
              پاک‌کردن فیلترها
            </button>
          </div>
        </div>

        {error ? (
          <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-sm text-red-800">{error}</p>
              <button
                type="button"
                className="text-xs font-bold text-red-700 hover:underline"
                onClick={() => setError('')}
              >
                بستن
              </button>
            </div>
          </div>
        ) : null}

        <div className="rounded-2xl bg-white p-4 shadow-sm">
          {loading ? (
            <div className="overflow-hidden rounded-2xl border border-indigo-100 bg-gradient-to-b from-indigo-50/60 via-white to-white">
              <div className="flex items-center justify-between border-b border-indigo-100/70 px-4 py-3">
                <div className="flex items-center gap-2 text-indigo-700">
                  <BrandSpinnerRing size="sm" />
                  <span className="text-sm font-bold">در حال بارگذاری دسته‌ها</span>
                </div>
              </div>
              <div className="space-y-2 p-3">
                {Array.from({ length: 8 }).map((_, idx) => (
                  <div
                    key={idx}
                    className="rounded-xl border border-slate-100 bg-white px-4 py-3.5 shadow-sm animate-pulse"
                  >
                    <div className="flex gap-3">
                      <div className="h-10 w-10 shrink-0 rounded-xl bg-slate-200" />
                      <div className="flex-1 space-y-2 py-1">
                        <div className="h-4 w-1/3 max-w-[12rem] rounded bg-slate-200" />
                        <div className="h-3 w-2/3 max-w-md rounded bg-slate-100" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : categoryTree.length === 0 ? (
            <div className="rounded-xl py-14 text-center">
              <SchoolRoundedIcon sx={{ fontSize: 48, color: '#94A3B8', mb: 2 }} />
              <p className="text-base font-semibold text-gray-900">هنوز دسته‌ای در این بخش نیست</p>
              <p className="mx-auto mt-2 max-w-md text-sm text-gray-600">
                اولین دسته را بسازید؛ سپس زیردسته‌ها را با ویرایش یا جابه‌جایی مرتب کنید.
              </p>
              <button
                type="button"
                onClick={() => openCreate()}
                className="mx-auto mt-4 inline-flex items-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-indigo-700"
              >
                دستهٔ جدید
              </button>
            </div>
          ) : filteredTree.length === 0 ? (
            <div className="rounded-xl py-12 text-center text-sm">
              <p className="font-semibold text-gray-800">هیچ دسته‌ای با این فیلترها یافت نشد.</p>
              <button
                type="button"
                className="mt-3 rounded-xl border border-gray-200 px-4 py-2 text-xs font-bold text-gray-700 hover:bg-gray-50"
                onClick={clearListFilters}
              >
                پاک‌کردن فیلترها
              </button>
            </div>
          ) : (
            <div>
              <div className="mb-4 flex items-center justify-between gap-2">
                <span className="text-sm font-bold text-gray-900">ساختار درختی</span>
                <div className="hidden flex-1 border-t border-slate-100 sm:block" />
              </div>
              <ul className="space-y-3">
                {filteredTree.map((node) => (
                  <StudioTreeNode
                    key={node.id}
                    node={node as CatTree}
                    depth={0}
                    contentType={activeTab}
                    onEdit={openEdit}
                    onMove={(c) => {
                      setMoveTarget(c);
                      setMoveParentId(c.parentId || '');
                    }}
                    onDelete={handleDelete}
                  />
                ))}
              </ul>
            </div>
          )}
        </div>

        <CategoryFormDialogs manager={manager} />
      </div>
    </ThemeProvider>
  );
};

export default CategoryStudioView;
